import { Injectable, inject } from "@angular/core";
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { Observable, from, throwError } from "rxjs";
import { catchError, finalize, switchMap } from "rxjs/operators";
import { MitpManagerService } from "./mitp-manager.service";
import { AuthService } from "./auth.service";
import { NotificationService } from "./notification.service";
import { HttpLoadingService } from "./http-loading.service";
import { SKIP_ERROR_TOAST, SKIP_LOADING } from "./http-context-tokens";
import { MaintenanceService } from "./maintenance.service";
import { MAINTENANCE_KEY } from "../interfaces/api-error";

@Injectable({
	providedIn: "root",
})
export class HttpInterceptorService implements HttpInterceptor {
	mitpManager = inject(MitpManagerService);
	private authService = inject(AuthService);
	private toastr = inject(NotificationService);
	private httpLoading = inject(HttpLoadingService);
	private maintenance = inject(MaintenanceService);

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		if (this.authService.logoutIfSessionExpired()) {
			this.mitpManager.showLoading(false);
			this.mitpManager.openErrorDialog("Votre session a expiré ! Merci de vous reconnecter.");
			return throwError(
				() =>
					new HttpErrorResponse({
						status: 401,
						statusText: "Expired JWT Token",
						error: { message: "Expired JWT Token" },
					})
			);
		}
		const handled = next
			.handle(req)
			.pipe(catchError((error) => this.handleError(error, req.context.get(SKIP_ERROR_TOAST))));
		if (req.context.get(SKIP_LOADING)) {
			return handled;
		}
		this.httpLoading.start();
		return handled.pipe(finalize(() => this.httpLoading.stop()));
	}

	handleError(error: HttpErrorResponse, skipToast = false): Observable<never> {
		// A file download (responseType "blob") receives its JSON error as a Blob: read it first, so that its message
		// is shown like any other.
		if (error.error instanceof Blob && error.error.type.includes("json")) {
			return from(this.readText(error.error)).pipe(
				switchMap((text) => this.handleError(this.withJsonBody(error, text), skipToast))
			);
		}
		this.mitpManager.showLoading(false);
		if (error.status == 401) {
			this.authService.logoutIfSessionExpired();
		}
		// The maintenance screen says it all: no toast or dialog on top of it.
		if (error.status == 503 && error.error?.key === MAINTENANCE_KEY) {
			this.maintenance.update(error.error.maintenance);
			return throwError(() => error);
		}
		if (error.error?.message != undefined) {
			switch (error.error.message) {
				case "Expired JWT Token":
					this.mitpManager.openErrorDialog("Votre session a expiré ! Merci de vous reconnecter.");
					break;
				case "Invalid credentials.":
					this.mitpManager.openErrorDialog("Indentifiants incorrects !");
					break;
				default:
					if (this.authService.isAuth() && !skipToast) {
						this.toastr.error(error.error.message);
					}
			}
		}
		this.mitpManager.showLoading(false);
		return throwError(() => error);
	}

	private readText(blob: Blob): Promise<string> {
		return new Promise((resolve) => {
			const reader = new FileReader();
			reader.onload = () => resolve(String(reader.result ?? ""));
			reader.onerror = () => resolve("");
			reader.readAsText(blob);
		});
	}

	private withJsonBody(error: HttpErrorResponse, text: string): HttpErrorResponse {
		let body: unknown;
		try {
			body = JSON.parse(text);
		} catch {
			body = text;
		}

		return new HttpErrorResponse({
			error: body,
			headers: error.headers,
			status: error.status,
			statusText: error.statusText,
			url: error.url ?? undefined,
		});
	}
}
